import { useEffect } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  ogImage?: string;
  schemaData?: Record<string, unknown>;
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalUrl = 'https://xukonghanman.com',
  ogTitle,
  ogDescription,
  ogType = 'website',
  ogImage,
  schemaData,
}: SEOHeadProps) {
  const pageTitle = `${title} - 虚空韩漫网`;
  const displayOgTitle = ogTitle || pageTitle;
  const displayOgDesc = ogDescription || description;

  useEffect(() => {
    document.title = pageTitle;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    setMeta('keywords', keywords);

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', canonicalUrl);

    setMeta('og:title', displayOgTitle, true);
    setMeta('og:description', displayOgDesc, true);
    setMeta('og:type', ogType, true);
    setMeta('og:url', canonicalUrl, true);
    setMeta('og:site_name', '虚空韩漫网', true);
    setMeta('og:locale', 'zh_CN', true);

    if (ogImage) {
      setMeta('og:image', ogImage, true);
    }

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', displayOgTitle);
    setMeta('twitter:description', displayOgDesc);
    if (ogImage) {
      setMeta('twitter:image', ogImage);
    }

    let schemaScript: HTMLScriptElement | null = null;
    if (schemaData) {
      const existing = document.querySelector('script[data-schema="dynamic"]');
      if (existing) existing.remove();
      schemaScript = document.createElement('script');
      schemaScript.type = 'application/ld+json';
      schemaScript.setAttribute('data-schema', 'dynamic');
      schemaScript.textContent = JSON.stringify(schemaData, null, 2);
      document.head.appendChild(schemaScript);
    }

    return () => {
      if (schemaScript) schemaScript.remove();
    };
  }, [pageTitle, description, keywords, canonicalUrl, displayOgTitle, displayOgDesc, ogType, ogImage, schemaData]);

  return null;
}

export function injectSchema(schemaData: Record<string, unknown>) {
  const existing = document.querySelector('script[data-schema="inline"]');
  if (existing) existing.remove();
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute('data-schema', 'inline');
  script.textContent = JSON.stringify(schemaData, null, 2);
  document.head.appendChild(script);
}